import React, { useEffect, useRef } from "react";
import { useAppDispatch } from "../redux/hooks";
import { authInitialized } from "../redux/reduxAuth/authSlice";
import {
  installAuthInterceptors,
  refresh,
  getAccessToken,
} from "./authService";
import { jwtDecode } from "jwt-decode";

/**
 * Bootstraps auth on load:
 *  - installs the API interceptors,
 *  - attempts a silent refresh (the HttpOnly cookie is sent automatically);
 *    success restores the session with no login screen, failure leaves the
 *    user unauthenticated,
 *  - schedules a proactive refresh shortly before each access token expires.
 *
 * It always renders its children; App decides what to show from redux auth
 * state (mirrors the previous Keycloak-provider flow).
 */
function scheduleRefresh(onRefreshed: () => void, timer: React.MutableRefObject<number | null>): void {
  const token = getAccessToken();
  if (!token) return;
  let expSeconds: number | undefined;
  try {
    expSeconds = jwtDecode<{ exp?: number }>(token).exp;
  } catch {
    return;
  }
  if (!expSeconds) return;
  // Refresh 60s before expiry (floor at 5s so a short token still refreshes).
  const msUntilRefresh = Math.max(expSeconds * 1000 - Date.now() - 60_000, 5_000);
  if (timer.current) window.clearTimeout(timer.current);
  timer.current = window.setTimeout(onRefreshed, msUntilRefresh);
}

function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const dispatch = useAppDispatch();
  const timer = useRef<number | null>(null);

  useEffect(() => {
    installAuthInterceptors();
    let cancelled = false;

    const tick = async () => {
      try {
        await refresh();
        if (cancelled) return;
        scheduleRefresh(tick, timer);
      } catch {
        // Silent — no valid session; App will show the login screen.
      }
    };

    (async () => {
      await tick();
      if (!cancelled) dispatch(authInitialized(true));
    })();

    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}

export default AuthProvider;
